import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Plus, Save, Trash2, X, Check, Loader2, Lock, RefreshCcw } from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";

// V1.38 · Panel Configuración → Contabilidad → Secciones
// CRUD completo + PIN Maestro para modificar el contador propio de la sección.
export default function AccountingSectionsSettings() {
  const pin = usePinSecurity();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {} para nueva, doc para editar
  const [counterEditingId, setCounterEditingId] = useState(null);
  const [counterDraft, setCounterDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/sections");
      setItems(data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (s) => {
    if (s.code === "01") {
      toast.error("La sección semilla '01 · General' no se puede eliminar.");
      return;
    }
    try {
      await pin.require("accounting.section.delete");
      if (!window.confirm(`¿Eliminar la sección ${s.code} · ${s.name}?`)) return;
      await api.delete(`/accounting/sections/${s.id}`);
      toast.success("Sección eliminada");
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  const saveCounter = async (s) => {
    const value = parseInt(counterDraft, 10);
    if (isNaN(value) || value < 0) {
      toast.error("Valor de contador inválido");
      return;
    }
    try {
      await pin.require("accounting.section.counter.update");
      await api.patch(`/accounting/sections/${s.id}/counter`, { value });
      toast.success(`Contador de sección ${s.code} actualizado a ${value}`);
      setCounterEditingId(null);
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  return (
    <div className="card-surface p-4" id="settings-accounting-sections" data-testid="accounting-sections-settings">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-orange-100 p-2 text-orange-700">
            <Layers className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Contabilidad · Secciones</h3>
            <p className="text-xs text-neutral-500">
              Dimensión transversal (franquicia / centro / línea de negocio). Cada gasto,
              proveedor y cuenta pertenece a una sección.
            </p>
          </div>
        </div>
        <button
          onClick={() => setEditing({})}
          className="btn-primary !h-9 !px-3 !text-xs"
          data-testid="section-new-btn"
        >
          <Plus className="h-4 w-4" /> Nueva sección
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-center text-sm text-neutral-500">
          <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "80px" }} />
              <col />
              <col style={{ width: "90px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "140px" }} />
            </colgroup>
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Nombre</th>
                <th className="text-left px-4 py-3">Orden</th>
                <th className="text-left px-4 py-3">Activa</th>
                <th className="text-left px-4 py-3">Contador</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const editingCounter = counterEditingId === s.id;
                return (
                  <tr key={s.id} className="border-t border-neutral-100 h-[56px]" data-testid={`section-row-${s.id}`}>
                    <td className="px-4 py-3 font-bold tabular-nums align-middle">{s.code}</td>
                    <td className="px-4 py-3 align-middle">
                      <div className="font-medium truncate" title={s.name}>{s.name}</div>
                      {s.code === "01" && (
                        <span className="text-[9px] uppercase tracking-wider bg-neutral-200 text-neutral-600 px-1.5 rounded">semilla</span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-neutral-500 align-middle">{s.order ?? 0}</td>
                    <td className="px-4 py-3 align-middle">
                      {s.active !== false ? (
                        <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                          <Check className="h-3 w-3" /> Activa
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-neutral-400 text-xs">
                          <X className="h-3 w-3" /> Inactiva
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      {s.use_global_counter ? (
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                          Global corporativo
                        </span>
                      ) : editingCounter ? (
                        <div className="inline-flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            value={counterDraft}
                            onChange={(e) => setCounterDraft(e.target.value)}
                            className="w-24 h-8 rounded border border-orange-300 px-2 text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500"
                            data-testid={`section-counter-input-${s.id}`}
                            autoFocus
                          />
                          <button onClick={() => saveCounter(s)}
                                  className="p-1 rounded bg-orange-500 text-white hover:bg-orange-600"
                                  data-testid={`section-counter-save-${s.id}`}>
                            <Save className="h-3 w-3" />
                          </button>
                          <button onClick={() => setCounterEditingId(null)}
                                  className="p-1 rounded hover:bg-neutral-100">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setCounterDraft(String(s.own_counter_last || 0));
                            setCounterEditingId(s.id);
                          }}
                          className="inline-flex items-center gap-1 text-xs text-orange-700 hover:underline"
                          data-testid={`section-counter-edit-${s.id}`}
                        >
                          <span className="font-bold tabular-nums">#{s.own_counter_last || 0}</span>
                          <Lock className="h-3 w-3" />
                          <span className="text-[10px] uppercase tracking-wider text-neutral-500">Editar (PIN)</span>
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <button onClick={() => setEditing(s)}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-neutral-100"
                              data-testid={`section-edit-${s.id}`}>
                        Editar
                      </button>
                      {s.code !== "01" && (
                        <button onClick={() => del(s)}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-red-50 text-red-600"
                                data-testid={`section-del-${s.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <SectionEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <div className="mt-3 flex items-center justify-between text-[11px] text-neutral-500">
        <div className="flex items-center gap-1.5">
          <RefreshCcw className="h-3 w-3" />
          <span>{items.length} sección{items.length === 1 ? "" : "es"} registrada{items.length === 1 ? "" : "s"}</span>
        </div>
        <div>Al arrancar se garantiza la sección semilla <b>01 · General</b> y se migran los registros existentes.</div>
      </div>
    </div>
  );
}

function SectionEditor({ initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    code: initial?.code || "",
    name: initial?.name || "",
    active: initial?.active !== false,
    order: initial?.order ?? 10,
    use_global_counter: initial?.use_global_counter !== false,
    own_counter_last: initial?.own_counter_last ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Código y nombre obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/accounting/sections", form);
        toast.success("Sección creada");
      } else {
        // No enviamos own_counter_last aquí: se cambia con el flujo PIN.
        const { own_counter_last, ...patch } = form;
        void own_counter_last;
        await api.patch(`/accounting/sections/${initial.id}`, patch);
        toast.success("Sección actualizada");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="section-editor-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{isNew ? "Nueva sección" : "Editar sección"}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-[100px_1fr] gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">Código *</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="01"
                maxLength={8}
                className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="section-form-code"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">Nombre *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Natur Autoescuela Valdemoro"
                className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="section-form-name"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 block mb-1">Orden</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })}
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="section-form-order"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                   data-testid="section-form-active" />
            <span>Sección activa</span>
          </label>
          <div className="rounded-xl border border-neutral-200 p-3 bg-neutral-50/50">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.use_global_counter}
                     onChange={(e) => setForm({ ...form, use_global_counter: e.target.checked })}
                     data-testid="section-form-use-global-counter" className="mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Usar contador global corporativo</div>
                <div className="text-[11px] text-neutral-500">
                  Si se desactiva, esta sección tendrá su propia numeración de gastos
                  (000001, 000002…). El contador propio solo se puede modificar con
                  PIN Maestro desde la lista.
                </div>
              </div>
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary !h-9 !px-3 !text-xs">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary !h-9 !px-4 !text-xs" data-testid="section-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
